import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Invoice } from "src/types/index";

interface Props {
    canDelete: Invoice[];
    cannotDelete: Invoice[];
}

type TabKey = "delete" | "skip";

function InvoiceRow({ inv }: { inv: Invoice }) {
    const r = inv.recipient;
    const name = r ? `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() : "—";
    return (
        <>
            <strong className="bsp-grid__id">{inv.readableId}</strong>
            <span className="bsp-grid__info">
                <span className="bsp-grid__info-name">{name}</span>
            </span>
            <span className="bsp-grid__cost-pages">€{inv.amount?.toFixed(2)}</span>
        </>
    );
}

export default function BulkDeletePreview({ canDelete, cannotDelete }: Props) {
    const { t } = useTranslation();

    const tabs: { key: TabKey; label: string; items: Invoice[] }[] = [
        canDelete.length > 0    && { key: "delete", label: t("invoices.bulkDelete.willDelete",   { count: canDelete.length }),    items: canDelete },
        cannotDelete.length > 0 && { key: "skip",   label: t("invoices.bulkDelete.cannotDelete", { count: cannotDelete.length }), items: cannotDelete },
    ].filter(Boolean) as { key: TabKey; label: string; items: Invoice[] }[];

    const [active, setActive] = useState<TabKey>(tabs[0]?.key ?? "delete");
    const current = tabs.find((tab) => tab.key === active);

    return (
        <div className="bsp">
            <div className="bsp__tabs" role="tablist">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={active === tab.key}
                        className={`bsp__tab bsp__tab--${tab.key}${active === tab.key ? " bsp__tab--active" : ""}`}
                        onClick={() => setActive(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            <div className="bsp__panel" role="tabpanel">
                {current && (
                    <div className="bsp-grid bsp-grid--link">
                        <span className="bsp-grid__hdr">{t("table.id")}</span>
                        <span className="bsp-grid__hdr">{t("table.recipient")}</span>
                        <span className="bsp-grid__hdr bsp-grid__hdr--r">{t("table.amount")}</span>
                        {current.items.map((inv) => <InvoiceRow key={inv.id} inv={inv} />)}
                    </div>
                )}
            </div>
        </div>
    );
}
